import { FileImage } from 'lucide-react';

import React, { useState } from 'react';

import { isPdfReceiptUrl } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

import { ReceiptPdfTile } from '@/components/expenses/receipt/ReceiptPdfTile';

import { ExpenseReceiptModal } from './ExpenseReceiptModal';

interface ExpenseReceiptThumbnailProps {
  /** Thumbnail URL (256px optimized version) */
  thumbnailUrl: string;
  /** Original full-size URL for viewing */
  originalUrl?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Test ID */
  'data-testid'?: string;
}

const SIZE_CLASSES = {
  sm: 'w-10 h-10',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
};

/**
 * Small clickable thumbnail for expense receipts.
 * Shows the optimized thumbnail and opens a modal with the full-size original when clicked.
 */
export const ExpenseReceiptThumbnail: React.FC<
  ExpenseReceiptThumbnailProps
> = ({ thumbnailUrl, originalUrl, size = 'md', 'data-testid': dataTestId }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pdf =
    isPdfReceiptUrl(thumbnailUrl) || isPdfReceiptUrl(originalUrl ?? '');

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`
          relative ${SIZE_CLASSES[size]} rounded-lg overflow-hidden
          border border-gray-200 dark:border-gray-700
          hover:border-orange-400 dark:hover:border-orange-500
          transition-all duration-200 hover:scale-105
          focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2
          dark:focus:ring-offset-gray-900
        `}
        title={t('expenses:photo.viewReceipt')}
        aria-label={pdf ? t('expenses:photo.viewReceipt') : undefined}
        data-testid={dataTestId}
      >
        {pdf ? (
          <ReceiptPdfTile />
        ) : (
          <>
            <img
              src={thumbnailUrl}
              alt={t('expenses:photo.receiptAlt')}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors" />
            <div className="absolute bottom-0 right-0 p-0.5 bg-black/50 rounded-tl">
              <FileImage className="w-2.5 h-2.5 text-white" />
            </div>
          </>
        )}
      </button>

      <ExpenseReceiptModal
        isOpen={isModalOpen}
        onClose={handleClose}
        imageUrl={originalUrl || thumbnailUrl}
        thumbnailUrl={thumbnailUrl}
        kind={pdf ? 'pdf' : 'image'}
      />
    </>
  );
};
